import fetch from 'isomorphic-fetch';
import { Dispatch } from "redux";
import { ErnotsConfig } from '../ErnotsConfig';
import { loginActions } from '../features/login/slice';
import { requestActions } from './slice';
import { makePlainObject } from '../utils';

let csrfToken = null as string|null;
let csrfTokenRequestOngoing = false;

/**
 * This function fetches the CSRF token that is necessary for POST requests from the server.
 * Also gets the logged-in user name and adds it to the state.
 * 
 * @param dispatch A function that updates the store by dispatching an action.
 */
export async function initializeCsrf(dispatch: Dispatch, lifeCycleCallback?: (rs: RequestStatus) => any)
{
    if (!csrfToken && !csrfTokenRequestOngoing)
    {
        fetchCsrf(dispatch, lifeCycleCallback);
    }

    while  (csrfTokenRequestOngoing)
    {
        await new Promise(resolve => setTimeout(resolve, 10));
    }
    return csrfToken;
}

async function fetchCsrf(dispatch: Dispatch, lifeCycleCallback?: (rs: RequestStatus) => any)
{
    const reqStatus = new RequestStatus();
    reqStatus.message = "Connecting to server...";

    dispatch(requestActions.incrementOngoingRequests());
    lifeCycleCallback?.(makePlainObject(reqStatus));

    try
    {
        const request = fetch(`${ErnotsConfig.APIURLPrefix}initialize/`,
        {
            method: 'GET',
            mode: 'cors',
            credentials: 'include',
        });
        csrfTokenRequestOngoing = true;

        const response = await request;
        const json = await response.json();

        reqStatus.responseJson = json;
        reqStatus.finished = true;
        reqStatus.statusCode = response.status;
        reqStatus.success = response.ok;
        reqStatus.message = 'Success.';
        
        dispatch(requestActions.decrementOngoingRequests());
        dispatch(loginActions.setCurrentUser(json.user));
        dispatch(requestActions.setBackendUnreachable(false));
        lifeCycleCallback?.(makePlainObject(reqStatus));

        csrfToken = json.csrf_token;
        csrfTokenRequestOngoing = false;
    }
    catch(e)
    {
        reqStatus.finished = true;
        reqStatus.statusCode = 500;
        reqStatus.success = false;
        reqStatus.message = 'Could not connect to server.';
        dispatch(requestActions.decrementOngoingRequests());
        dispatch(requestActions.setBackendUnreachable(true));
        lifeCycleCallback?.(makePlainObject(reqStatus));

        csrfToken = null;
        csrfTokenRequestOngoing = false;
    }
}

export async function makeApiRequest(args: ApiRequestArgs)
{
    const
    {
        endpoint,
        method,
        postData,
        useAuthPrefix,
        requireStatus,
        blob,
        lifeCycleCallback,
        dispatch,
    } = args;

    //Initialize reqStatus.
    const reqStatus = new RequestStatus();
    
    reqStatus.message = 'Please wait...';
    dispatch(requestActions.incrementOngoingRequests());
    lifeCycleCallback?.(makePlainObject(reqStatus));
    
    //Ensure there's a csrf_token.
    const csrfToken = await initializeCsrf(dispatch);
    if (!csrfToken)
    {
        reqStatus.finished = true;
        reqStatus.message = "Could not obtain CSRF token. Server may be unavailable.";
        dispatch(requestActions.decrementOngoingRequests());
        lifeCycleCallback?.(makePlainObject(reqStatus));
        return reqStatus;
    }

    let json = null as string|null;
    if (!['GET', 'HEAD'].includes(method.toUpperCase()))
    {
        //Combine postData with csrf.
        const body = 
        {
            'csrfmiddlewaretoken': csrfToken,
            ...postData
        }

        json = JSON.stringify(body);
    }

    try
    {
        const urlPrefix = useAuthPrefix ? ErnotsConfig.AuthURLPrefix : ErnotsConfig.APIURLPrefix;
        const url = `${urlPrefix}${endpoint}`;

        //Send.
        const response = await fetch(url,
        {
            method: method,
            mode: 'cors',
            credentials: 'include',
            body: json,
            headers:
            {
                "Content-Type": "application/json",
                //Ensure we get a JSON response, instead of one of the Django API test forms.
                'Accept': 'application/json', 
                //For request methods that don't have a body, the token needs to be sent like this.
                "X-Csrftoken": csrfToken,
            },
        });
        
        //Receive.
        reqStatus.finished = true;
        reqStatus.statusCode = response.status;
        reqStatus.success = response.ok;
        if (requireStatus && response.status !== requireStatus)
        {
            reqStatus.success = false;
        }

        //Get response text/blob.
        try
        {
            if (blob)
            {
                const blob = await response.blob();
                reqStatus.responseBlob = URL.createObjectURL(blob);
            }
            else
            {
                reqStatus.responseText = await response.text();
                try { reqStatus.responseJson = JSON.parse(reqStatus.responseText); } catch(e) { }
            }
        }
        catch(e)
        {
            reqStatus.success = false;
        }

        if (reqStatus.success)
        {
            reqStatus.message = 'Success!';
        }
        //When the status corresponds to an error, the message returned by the server should provide an explanation.
        else
        {
            if (reqStatus.responseJson && typeof reqStatus.responseJson == 'object')
            {
                reqStatus.message = `Error:
${formatErrorDictionary(reqStatus.responseJson)}`;
            }
            else if (reqStatus.responseText)
            {
                reqStatus.message = 'Error: ' + reqStatus.responseText;
            }
            else
            {
                reqStatus.message = 'Error: Unknown error';
            }
        }
        
        dispatch(requestActions.decrementOngoingRequests());
        dispatch(requestActions.setBackendUnreachable(false));
        lifeCycleCallback?.(makePlainObject(reqStatus));
        return reqStatus;
    }
    catch (e)
    {
        let reqStatus = new RequestStatus();
        reqStatus.finished = true;
        reqStatus.message = 'Could not connect to server.';

        dispatch(requestActions.decrementOngoingRequests());
        dispatch(requestActions.setBackendUnreachable(true));
        lifeCycleCallback?.(makePlainObject(reqStatus));
        return reqStatus;
    }
}

/** Removes the CSRF token, forcing the next API request to get a new one. */
export function unsetCsrfToken()
{
    csrfToken = null;
}

/**
 * The normal way to play a sound is to just create an Audio element and set its src.
 * But we need to send a header (X-Csrftoken), and apparently, there is no easy way to do it.
 * I reuse my general method makeApiRequest. That means endpoint should not include the URLprefix.
 * 
 * Note the use of namespaced attributes "ready", "error" and "endpoint".
 * 
 * The "audio" aegument gives us a way to retry a request and put the result in the same element that previously failed.
 */
export function requestAudio(endpoint: string, dispatch: Dispatch, audio?: HTMLAudioElement)
{
    const audio2 = audio ? audio : new Audio();

    audio2.removeAttributeNS('ernots', 'ready');
    audio2.removeAttributeNS('ernots', 'error');
    audio2.setAttributeNS('ernots', 'endpoint', endpoint);
    audio2.setAttributeNS('ernots', 'creationTime', Date.now().toString());

    const req = makeApiRequest(
        {
            endpoint,
            dispatch,
            method: 'GET',
            lifeCycleCallback: () => {},
            blob: true
        });
    req
        .then(function(req: RequestStatus)
        {
            const blob = req.responseBlob;
            if (blob)
            {
                audio2.src = blob;
                audio2.setAttributeNS('ernots', 'ready', 'true');
                audio2.dispatchEvent(new Event('canplaythrough'));
            }
            else
            {
                audio2.setAttributeNS('ernots', 'error', 'No data fetched');
                audio2.dispatchEvent(new Event('error'));
            }
        })
        .catch(function(error)
        {
            audio2.setAttributeNS('ernots', 'error', `${error}`);
            audio2.dispatchEvent(new Event('error'));
        });

    return audio2;
}

/**When you submit a form, Django sometimes returns errors in the form of a mapping from fields to what went wrong with them.*/
function formatErrorDictionary(dict: object)
{
    const ret0 = Object.entries(dict);
    const ret1 = ret0.map(ent => `  ${ent[0]}: ${ent[1]}`);
    const ret = ret1.join(`
`);

    return ret;
}
export class RequestStatus {
    /** True if the request has either succeeded or failed, false if it's incomplete. */
    finished = false;

    /** The status code of the HTTP response sent by the server. */
    statusCode = null as null | number;

    /** Whether the request succeeded. Null if it's unfinished. */
    success = null as null | boolean;

    /** A message to display in UI. */
    message = '';

    /** The body of the response, if text was requested. */
    responseText = null as null | string;

    /** The body of the response, if a blob was requested. The blob is converted to ObjectURL.*/
    responseBlob = null as null | string;

    /** If the response can be parsed as JSON, the response is placed here. Else null. */
    responseJson = null as any;

    /** The object or objects downloaded by the request (if applicable) have been added to the React-Redux state store. */
    inState = false;
}

type ApiRequestArgs = 
{
    /** The URL of the API endpoint, minus the prefix defined by Ernots.APIURLPrefix. */
    endpoint: string,
    
    /** HTTP method to use. */
    method: string,

    /** A map of arguments to send along with the request. */
    postData?: {[key: string]: any},

    /** If true, use AuthURLPrefix (for /auth requests) instead of AuthURLPrefix (for my custom REST API). */
    useAuthPrefix?: boolean,

    /** Set success=false if the response status isn't this. If unset, accept any 2** status. */
    requireStatus?: number,

    /**
     * If true, use URL.createObjectURL(blob) on responseText and put it in responseBlob.
     * Otherwise, try to parse it as json and put the result in responseJson.
     */
    blob?: boolean,

    /** A callback that keeps track of the current status. */
    lifeCycleCallback: (rs: RequestStatus) => any,

    /**
     * A function that updates the Redux store.
     * This helps us record data about the request that is useful throughout the program.
     */
    dispatch: Dispatch,
}